import { useState, useEffect, useRef, useCallback } from 'react'
import type { Vulnerability, VulnerabilityStats, TimeRange } from '../types/vulnerability'
import type { WorkerResponse, FilterCriteria, ColumnFilter, ChartBucket } from '../workers/dataWorker'

interface UseWorkerFilterResult {
  filteredData: Vulnerability[]
  filteredStats: VulnerabilityStats | null
  legendCounts: Record<string, number> | null  // Severity counts before severity filter (for legend)
  chartData: ChartBucket[]
  isFiltering: boolean
}

interface UseWorkerFilterProps {
  worker: Worker | null  // Shared worker from useVulnerabilityData
  data: Vulnerability[]
  excludeManual: boolean
  excludeAi: boolean
  searchPills: string[]
  severityFilters: string[]
  columnFilters: ColumnFilter[]
  timeRange: TimeRange
  timeBucket: string | null
}

/**
 * Hook to perform filtering using a shared Web Worker
 *
 * Uses the same worker that loaded the data, so:
 * - No duplicate data loading (saves memory + time)
 * - No race conditions (worker already has data)
 * - Cleaner architecture
 *
 * Now includes time range filtering and stats computation in the worker,
 * so the main thread doesn't block during filtering.
 */
export function useWorkerFilter({
  worker,
  data,
  excludeManual,
  excludeAi,
  searchPills,
  severityFilters,
  columnFilters,
  timeRange,
  timeBucket,
}: UseWorkerFilterProps): UseWorkerFilterResult {
  const [filteredData, setFilteredData] = useState<Vulnerability[]>(data)
  const [filteredStats, setFilteredStats] = useState<VulnerabilityStats | null>(null)
  const [legendCounts, setLegendCounts] = useState<Record<string, number> | null>(null)
  const [chartData, setChartData] = useState<ChartBucket[]>([])
  const [isFiltering, setIsFiltering] = useState(false)
  const requestIdRef = useRef<string>('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dataRef = useRef<Vulnerability[]>(data)  // Ref for filtering in message handler

  // Keep dataRef in sync
  dataRef.current = data

  // Listen for filter responses from shared worker
  useEffect(() => {
    if (!worker) return

    const handleMessage = (event: MessageEvent<WorkerResponse>) => {
      const response = event.data

      if (response.type === 'FILTER_COMPLETE') {
        if (response.requestId === requestIdRef.current) {
          // If passingIds is empty, it means all items pass (no filters active)
          // Otherwise, filter local data using the IDs from worker
          if (response.passingIds.length === 0) {
            setFilteredData(dataRef.current)
          } else {
            const idSet = new Set(response.passingIds)
            setFilteredData(dataRef.current.filter(v => idSet.has(v.id)))
          }
          setFilteredStats(response.stats)
          setLegendCounts(response.legendCounts)
          setChartData(response.chartData)
          setIsFiltering(false)
        }
      }
    }

    worker.addEventListener('message', handleMessage)

    return () => {
      worker.removeEventListener('message', handleMessage)
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [worker])

  // Request filtering when criteria change
  const requestFilter = useCallback(() => {
    if (!worker || data.length === 0) return

    const criteria: FilterCriteria = {
      excludeManual,
      excludeAi,
      searchPills,
      severityFilters,
      columnFilters,
      timeRange,
      timeBucket,
    }

    const requestId = `filter-${Date.now()}-${Math.random().toString(36).slice(2)}`
    requestIdRef.current = requestId

    setIsFiltering(true)
    worker.postMessage({
      type: 'FILTER_DATA',
      criteria,
      requestId,
    })
  }, [worker, data.length, excludeManual, excludeAi, searchPills, severityFilters, columnFilters, timeRange, timeBucket])

  // Debounce filter requests
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (data.length === 0) {
      setFilteredData([])
      setFilteredStats(null)
      setChartData([])
      return
    }

    // No worker yet = data still loading, show unfiltered
    if (!worker) {
      setFilteredData(data)
      return
    }

    // Always run through worker since time range filtering is always active
    // (even with no explicit filters, we need stats and time range applied)
    debounceRef.current = setTimeout(() => {
      requestFilter()
    }, 50)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [worker, data, excludeManual, excludeAi, searchPills, severityFilters, columnFilters, timeRange, timeBucket, requestFilter])

  return {
    filteredData,
    filteredStats,
    legendCounts,
    chartData,
    isFiltering,
  }
}
